/** Mirrors the backend contract in Appendix B. */

export const LOCALES = ['uz', 'ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/**
 * Stories feed the mobile app, which ships a 4th language — Chinese (zh) — that
 * the website (uz/ru/en) never had. Only story text is authored in Chinese, so
 * the extra locale lives here, separate from the website's `LOCALES`, and never
 * leaks a `zh` tab into the article/service editors.
 */
export const STORY_LOCALES = ['uz', 'ru', 'en', 'zh'] as const;
export type StoryLocale = (typeof STORY_LOCALES)[number];
export type StoryLocaleText = Record<StoryLocale, string | null>;

export type Completeness = 'full' | 'partial' | 'empty';
export type Status = 'DRAFT' | 'PUBLISHED';

/** A per-language text bag — the same shape the localised fields already use. */
export type LocaleText = Record<Locale, string | null>;

/** The role as carried on a user — name + the pages it may open. */
export interface UserRole {
  id: string;
  key: string;
  name: string;
  isSuperAdmin: boolean;
  permissions: string[];
}

export interface User {
  id: string;
  name: string;
  username: string | null;
  email: string | null;
  isActive?: boolean;
  mustChangePassword: boolean;
  lastLoginAt?: string | null;
  roleId: string | null;
  role: UserRole | null;
}

/** A role row as the /roles endpoint returns it (for the management screen). */
export interface RoleItem {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  isSuperAdmin: boolean;
  permissions: string[];
  userCount: number;
}

/** The page catalogue the /roles endpoint travels with. */
export interface PageDef {
  key: string;
  label: string;
  group: 'app' | 'content' | 'manage' | 'system';
}

export interface MediaVariant {
  url: string;
  w: number;
  h: number;
}

export interface MediaItem {
  id: string;
  url: string;
  filename: string;
  original: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
  variants: Record<string, MediaVariant> | null;
  alt: Record<Locale, string | null>;
  createdAt: string;
}

export interface SeoBlock {
  metaTitle: string | null;
  metaDesc: string | null;
  /** Comma-separated, normalised by the server on write. */
  metaKeywords: string | null;
  ogImage: { url: string; width: number | null; height: number | null } | null;
}

export interface CategoryRef {
  id?: string;
  key: string;
  slug: string;
  name: Record<Locale, string | null>;
}

export interface ArticleListItem {
  id: number;
  slug: string;
  title: Record<Locale, string | null>;
  status: Status;
  completeness: Record<Locale, Completeness>;
  hasCover: boolean;
  // cover + excerpt are served by the enriched admin list endpoint. They are
  // optional so the card view degrades gracefully if the API hasn't been
  // deployed yet (falls back to a placeholder cover / no description).
  cover?: { url: string; alt: Record<Locale, string | null> } | null;
  excerpt?: Record<Locale, string | null>;
  category: CategoryRef | null;
  publishedAt: string | null;
  updatedAt: string;
  updatedBy: { id: string; name: string } | null;
}

/** A cover image reference as the admin detail returns it (per locale). */
export interface CoverRef {
  id?: string;
  url: string;
  width: number | null;
  height: number | null;
  alt: Record<Locale, string | null>;
}

export interface ArticleDetail {
  id: number;
  slug: string;
  status: Status;
  localesWithContent: Locale[];
  completeness: Record<Locale, Completeness>;
  publishedAt: string | null;
  updatedAt: string;
  createdAt?: string;
  categoryId: string | null;
  category: CategoryRef | null;
  // Per-locale cover: coverId is uz/default, coverRuId/coverEnId override.
  coverId: string | null;
  coverRuId: string | null;
  coverEnId: string | null;
  // The editor reads exactly what each locale has (no fallback).
  cover: Record<Locale, CoverRef | null>;
  ogImageUzId: string | null;
  ogImageRuId: string | null;
  ogImageEnId: string | null;
  title: Record<Locale, string | null>;
  body: Record<Locale, unknown>;
  seo: Record<Locale, SeoBlock | null>;
  canonicalUrl: string | null;
  noIndex: boolean;
}

/** The flat editor model — one row, the way the API accepts a PATCH. */
export interface ArticleForm {
  slug: string;
  categoryId: string | null;
  coverId: string | null;
  titleUz: string | null;
  titleRu: string | null;
  titleEn: string | null;
  bodyUz: unknown;
  bodyRu: unknown;
  bodyEn: unknown;
  metaTitleUz: string | null;
  metaTitleRu: string | null;
  metaTitleEn: string | null;
  metaDescUz: string | null;
  metaDescRu: string | null;
  metaDescEn: string | null;
  ogImageUzId: string | null;
  ogImageRuId: string | null;
  ogImageEnId: string | null;
  canonicalUrl: string | null;
  noIndex: boolean;
}

export interface ServiceListItem {
  id: string;
  slug: string;
  title: Record<Locale, string | null>;
  icon: string | null;
  order: number;
  status: Status;
  completeness: Record<Locale, Completeness>;
  hasCover: boolean;
  // Optional: served by the enriched admin list endpoint. The card falls back
  // to an accent-gradient banner when no cover URL is present.
  cover?: { url: string; alt: Record<Locale, string | null> } | null;
  excerpt?: Record<Locale, string | null>;
  publishedAt: string | null;
  updatedAt: string;
}

export interface ServiceDetail extends Omit<ServiceListItem, 'hasCover' | 'cover'> {
  localesWithContent: Locale[];
  coverId: string | null;
  coverRuId: string | null;
  coverEnId: string | null;
  cover: Record<Locale, CoverRef | null>;
  excerpt: Record<Locale, string | null>;
  body: Record<Locale, unknown>;
  seo: Record<Locale, SeoBlock | null>;
  ogImageUzId: string | null;
  ogImageRuId: string | null;
  ogImageEnId: string | null;
  canonicalUrl: string | null;
  noIndex: boolean;
}

// ── Stories (Instagram-style rings for the mobile app) ────────────────────

/** A resolved media reference, as the admin story serializers return it. */
export interface MediaRef {
  id: string;
  url: string;
  width: number | null;
  height: number | null;
  variants: Record<string, MediaVariant> | null;
  alt: LocaleText;
}

export type StoryMediaType = 'IMAGE' | 'VIDEO';

export interface StorySlideDetail {
  id: string;
  order: number;
  mediaType: StoryMediaType;
  mediaId: string | null;
  posterId: string | null;
  media: MediaRef | null;
  poster: MediaRef | null;
  caption: StoryLocaleText;
  linkUrl: string | null;
  linkLabel: StoryLocaleText;
  durationSec: number;
}

export interface StoryDetail {
  id: string;
  order: number;
  status: Status;
  title: StoryLocaleText;
  coverId: string | null;
  cover: MediaRef | null;
  ttlHours: number;
  viewCount: number;
  publishedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  slides: StorySlideDetail[];
  createdAt: string;
  updatedAt: string;
  createdById: string | null;
  updatedById: string | null;
}

export interface StoryListItem {
  id: string;
  order: number;
  status: Status;
  title: StoryLocaleText;
  cover: MediaRef | null;
  slideCount: number;
  ttlHours: number;
  viewCount: number;
  publishedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  updatedAt: string;
}

export interface StorySlideInput {
  mediaType: StoryMediaType;
  mediaId?: string | null;
  posterId?: string | null;
  captionUz?: string | null;
  captionRu?: string | null;
  captionEn?: string | null;
  captionZh?: string | null;
  linkUrl?: string | null;
  linkLabelUz?: string | null;
  linkLabelRu?: string | null;
  linkLabelEn?: string | null;
  linkLabelZh?: string | null;
  durationSec?: number;
}

export interface StoryInput {
  titleUz?: string | null;
  titleRu?: string | null;
  titleEn?: string | null;
  titleZh?: string | null;
  coverId?: string | null;
  order?: number;
  ttlHours?: number;
  slides?: StorySlideInput[];
}

export interface CategoryItem {
  id: string;
  key: string;
  slug: string;
  name: Record<Locale, string | null>;
  body: Record<Locale, unknown>;
  seo: Record<Locale, SeoBlock | null>;
  completeness: Record<Locale, Completeness>;
  articleCount: number;
  order: number;
  canonicalUrl: string | null;
  noIndex: boolean;
  updatedAt: string;
}

export interface PageItem {
  id: string;
  route: string;
  heading: Record<Locale, string | null>;
  body: Record<Locale, unknown>;
  seo: Record<Locale, SeoBlock | null>;
  completeness: Record<Locale, Completeness>;
  isEmpty: boolean;
  canonicalUrl: string | null;
  noIndex: boolean;
  updatedAt: string;
}

export interface RedirectItem {
  id: string;
  from: string;
  to: string;
  statusCode: number;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
}

export type LeadStatus = 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CLOSED' | 'SPAM';

export interface LeadItem {
  id: string;
  name: string;
  contact: string;
  message: string | null;
  source: string | null;
  landingPath: string | null;
  referrer: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  locale: string | null;
  status: LeadStatus;
  isRead: boolean;
  createdAt: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string;
  diff: Record<string, { from: unknown; to: unknown }> | null;
  ip: string | null;
  createdAt: string;
  user: { id: string; name: string } | null;
}

export interface Overview {
  attention: {
    drafts: number;
    missingRu: number;
    missingEn: number;
    missingSeo: number;
    noCover: number;
    unreadLeads: number;
  };
  content: { drafts: number; published: number };
  recent: AuditEntry[];
  system: {
    storage: { driver: string; healthy: boolean };
    media: { count: number; bytes: number; unused: number };
  };
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

export interface Readiness {
  blockers: Array<{ field: string; message: string }>;
  warnings: Array<{ field: string; message: string }>;
}

export interface RevalidationResult {
  requested: string[];
  ok: boolean;
  error?: string;
}

/** The stable error identifiers the dashboard branches on. Never branch on
 *  `message` — it is user-facing text and may be reworded at any time. */
export type ApiErrorCode =
  | 'BAD_REQUEST' | 'UNSUPPORTED_TYPE' | 'UNAUTHENTICATED' | 'INVALID_CREDENTIALS'
  | 'REFRESH_INVALID' | 'FORBIDDEN' | 'PASSWORD_CHANGE_REQUIRED' | 'NOT_FOUND'
  | 'SLUG_TAKEN' | 'MEDIA_IN_USE' | 'ROUTE_NOT_SEEDED' | 'CONFLICT'
  | 'FILE_TOO_LARGE' | 'VALIDATION_FAILED' | 'NOT_PUBLISHABLE' | 'IMAGE_TOO_LARGE'
  | 'ACCOUNT_DISABLED' | 'RATE_LIMITED' | 'INTERNAL' | 'STORAGE_UNAVAILABLE';

export interface ApiErrorShape {
  code: ApiErrorCode;
  message: string;
  details?: Array<{ field: string; message: string }>;
  requestId?: string;
  retryAfter?: number;
  usedBy?: Array<{ entity: string; id: string | number; label: string }>;
  conflict?: {
    updatedAt: string;
    updatedBy: { id: string; name: string } | null;
    theirs: ArticleDetail;
  };
}

// ── RS skaner telemetriyasi ─────────────────────────────────────────────

export type ScanSource = 'PDA_SCANNER' | 'MANUAL' | 'CAMERA';
export type ScanKind = 'RS_RECEIVE' | 'SORTING_SCAN' | 'SORTING_STICKER';

export interface ScanEvent {
  id: string;
  clientId: string;
  code: string;
  kind: ScanKind;
  source: ScanSource;
  ok: boolean;
  errorText: string | null;
  username: string;
  role: string | null;
  deviceId: string | null;
  deviceModel: string | null;
  appVersion: string | null;
  /** Null whenever the scan happened where there was no fix — indoors, mostly. */
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  isMocked: boolean;
  capturedAt: string;
  receivedAt: string;
  clockSkewMs: number | null;
  durationMs: number | null;
}

export interface ScanStats {
  total: number;
  ok: number;
  failed: number;
  located: number;
  unlocated: number;
  suspicious: number;
  bySource: Partial<Record<ScanSource, number>>;
  byKind: Partial<Record<ScanKind, number>>;
  topUsers: Array<{ username: string; count: number }>;
}

/** A single scan, trimmed to what the map needs. */
export interface ScanMapPoint {
  id: string;
  code: string;
  kind: ScanKind;
  source: ScanSource;
  ok: boolean;
  username: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  isMocked: boolean;
  receivedAt: string;
}

/** Server-side aggregation, returned instead of points once the set is large. */
export interface ScanMapCell {
  lat: number;
  lng: number;
  count: number;
  failed: number;
  mocked: number;
}

export type ScanMapData =
  | { mode: 'points'; total: number; points: ScanMapPoint[] }
  | { mode: 'grid'; total: number; cellSize: number; cells: ScanMapCell[] };

export interface ScanFilters {
  /** Passed straight through as query params, so the shape must be indexable. */
  [key: string]: string | number | undefined;
  from?: string;
  to?: string;
  username?: string;
  kind?: ScanKind;
  source?: ScanSource;
  result?: 'ok' | 'failed';
  suspicious?: 'true';
  q?: string;
}
